// Public auth hook — consumed by pages and components.
//
// Wraps the Zustand store with the action methods that hit the API
// (login / logout / refresh-me). The store itself only holds state;
// network calls live here.

import { useCallback } from 'react';
import { apiClient } from '../api/client';
import { endpoints } from '../api/endpoints';
import { storage } from '../utils/storage';
import type { LoginResult, MeResult } from '../types/api';
import { useAuthStore } from '../store/authStore';

export interface LoginInput {
  username: string;
  password: string;
}

export function useAuth() {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const urlParamSecret = useAuthStore((s) => s.urlParamSecret);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);
  const setAnonymous = useAuthStore((s) => s.setAnonymous);
  const setLoading = useAuthStore((s) => s.setLoading);

  const login = useCallback(
    async (input: LoginInput) => {
      setLoading();
      const loginRes = await apiClient.post<LoginResult, LoginInput>(
        endpoints.auth.login,
        input,
      );
      const { access_token, refresh_token, user: loginUser } = loginRes.data;
      storage.set('accessToken', access_token);
      storage.set('refreshToken', refresh_token);
      storage.set('realm', loginUser.realm);
      if (loginUser.company_id !== null) {
        storage.set('companyId', String(loginUser.company_id));
      }

      // Pull /me to get the URL_PARAM_SECRET. We could decode the JWT, but
      // /me also re-checks the user is still active and returns the
      // canonical company_name.
      const meRes = await apiClient.get<MeResult>(endpoints.auth.me);
      setAuthenticated(meRes.data.user, meRes.data.url_param_secret);
      return meRes.data.user;
    },
    [setAuthenticated, setLoading],
  );

  const logout = useCallback(async () => {
    try {
      await apiClient.raw.post(endpoints.auth.logout);
    } catch {
      // Even on network failure we treat the user as logged out client-side.
    }
    // Only clear the tenant slots — a concurrent super-admin session in
    // the same browser must survive a tenant logout.
    storage.clearTenant();
    setAnonymous();
  }, [setAnonymous]);

  const refreshMe = useCallback(async () => {
    const meRes = await apiClient.get<MeResult>(endpoints.auth.me);
    setAuthenticated(meRes.data.user, meRes.data.url_param_secret);
    return meRes.data.user;
  }, [setAuthenticated]);

  return {
    status,
    user,
    urlParamSecret,
    isAuthenticated,
    login,
    logout,
    refreshMe,
  };
}
